import type { TableColumnType } from 'ant-design-vue';
import { ref, computed } from 'vue';

import { getOrderBy } from './functions';
import type { SorterProperties, TableSearchParameters } from './tableProperties';

import type TableColumnStorage from '@/util/localStorage/TableColumnStorage';

export type ColumnsAvailableValueProps<T extends string = string> = TableColumnType & {
  key: T;
  alwaysVisible?: boolean;
  hide?: boolean;
};

export type ColumnsAvailableProps<T extends string = string> = Partial<Record<T, ColumnsAvailableValueProps<T>>>;

interface UseColumnsProps {
  orderBy?: TableSearchParameters['orderBy'];
  storage: TableColumnStorage;
  columnsOrder: string[];
  columnsAvailables: ColumnsAvailableProps;
}
const useColumnsFilter = ({
  orderBy,
  storage,
  columnsOrder,
  columnsAvailables,
}: UseColumnsProps) => {
  const columnsSelected = ref(storage.get());
  const sortedInfo = ref<SorterProperties | undefined>(getOrderBy(orderBy));
  const setSorter = (sorter?: SorterProperties) => { sortedInfo.value = sorter; };

  const columnsFilter: ColumnsAvailableValueProps[] = [];
  columnsOrder.forEach((colName) => {
    const col = columnsAvailables[colName];
    if (col && !col.alwaysVisible && !col.hide) {
      columnsFilter.push(col);
    }
  });

  const columns = computed(() => {
    const sorted = sortedInfo.value;
    const cols: TableColumnType[] = [];
    if (storage) {
      storage.set(columnsSelected.value);
    }
    columnsOrder.forEach((colName) => {
      const col = columnsAvailables[colName];
      if (col && !col.hide && (col.alwaysVisible || columnsSelected.value.includes(colName))) {
        cols.push(
          col.sorter
            ? ({
              ...col,
              sortOrder: sorted && sorted.columnKey === col.key ? sorted.order : undefined,
            })
            : col,
        );
      }
    });
    return cols;
  });

  return {
    columnsSelected,
    sortedInfo,
    setSorter,
    columnsFilter,
    columns,
  };
};

export default useColumnsFilter;
